import { useMemo, useState } from 'react';
import { usePhoneCrumb } from '../lib/usePhoneCrumb';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Card, Chip, DChip, Icon, Modal, Pane, PaneHead, ReasonTable, type ReasonRow,
} from '@/components/kit';
import { useShellChrome } from '@/layouts/shellChrome';
import { usePageMeta } from '@/hooks/usePageMeta';
import apiClient from '@/services/apiClient';
import { useAuthStore } from '@/stores/useAuthStore';
import { ManagerSurface } from '../components/ManagerSurface';
import { ManagerMenu } from '../components/ManagerMenu';
import { useBranches, useMembers, type MemberRow } from '../lib/useManagerData';
import { stamp } from '../lib/scope';
import { useNarrowViewport } from '@/components/kit/useNarrow';
import { SettingsRail } from './SettingsRail';
import { MemberProfileCards } from './MemberProfileCards';
import { TextEditor } from './editors';
import { useMoneyPolicy, useSaveMoneyPolicy } from './useSettings';

/**
 * Clinic manager · Settings ▸ Roles & access — `g_set_roles.dc.html`.
 *
 * ONE HUMAN, ONE LOGIN, SEVERAL ROLES. The board's model is additive — "a role
 * is a switch, never a second account" — and so is the product's now: a
 * membership carries a PRIMARY role (the home screen, the sidebar) plus any
 * number of extras whose permissions are unioned into the same login. So the
 * switches on this pane are real switches, and throwing one takes effect on the
 * person's very next request rather than at their next sign-in.
 *
 * THE FIRST SWITCH IS THE PRIMARY, and it cannot be turned off from here —
 * changing which role somebody LANDS on is `PATCH members/:id/role`, the
 * owner's act, and is a different decision from "she also does the billing".
 *
 * WHAT A MANAGER MAY ACTUALLY DO HERE, from memberships.controller.ts:
 *   list members …………………… requireManagerOrOwner ✔
 *   invite a person ……………… requireManagerOrOwner ✔  (+ Add person)
 *   suspend / reactivate …… requireManagerOrOwner ✔, never an Owner or a
 *                            fellow Manager (assertManagerCannotTargetPrivileged)
 *   reset a password ……………… requireManagerOrOwner ✔, same exclusions
 *   CHANGE a role …………………… requireOwner ✘
 *   REVOKE a membership ……… requireOwner ✘
 * So the first four are live and the last two are marked as the owner's,
 * before the click rather than after it.
 *
 * THE PAYOUT CHIP IS THE SAME FIGURE MONEY EDITS. The board puts it on the
 * doctor's row "because it is a property of the person, and it is edited in
 * Money" — so it reads and writes `money.policy.payoutShares`, the one place
 * it lives.
 */

const ASSIGNABLE = [
  'Doctor', 'Receptionist', 'Billing', 'Nurse', 'Technician', 'Stock Manager', 'Accountant', 'Clinic Manager', 'Owner',
];

/** Roles a Clinic Manager may never create, promote to, or act on. */
const PRIVILEGED = new Set(['Owner', 'Clinic Manager']);

/** The switches `g_m_addp` draws, minus the two a manager may not grant. */
const INVITABLE = [
  'Doctor', 'Receptionist', 'Billing', 'Nurse',
  'Technician', 'Accountant', 'Stock Manager',
];

/** `g_m_revoke`'s table, in the board's order and its exact words. */
const REVOKE_REASONS: ReasonRow[] = [
  { key: 'left', label: 'Left the clinic',
    consequence: 'the login is closed and every session ends now' },
  { key: 'contract_ended', label: 'Contract ended',
    consequence: 'the login is closed; the person’s history stays where it is' },
  { key: 'moved_employer', label: 'Moved to another employer',
    consequence: 'closed, and the roles are freed for whoever replaces them' },
  { key: 'compromised', label: 'Device or password compromised',
    consequence: 'sessions end immediately — suspend first if they are still staff' },
  { key: 'duplicate', label: 'Duplicate account',
    consequence: 'closed and pointed at the account that stays' },
];

type InviteChannel = 'whatsapp' | 'email' | 'slip';

const CHANNELS: Array<{ key: InviteChannel; label: string }> = [
  { key: 'whatsapp', label: 'WhatsApp' },
  { key: 'email', label: 'Email' },
  { key: 'slip', label: 'Print a slip' },
];

/** Exactly what `POST /tenants/:id/invitations` takes. */
interface InviteBody {
  firstName: string;
  lastName?: string;
  phone?: string;
  email?: string;
  deliveryChannel: InviteChannel;
  roleName: string;
  additionalRoleNames: string[];
  /** Empty / omitted = every branch. */
  branchIds?: string[];
}

const fullName = (m: MemberRow) =>
  [m.firstName, m.lastName].filter(Boolean).join(' ').trim() || m.email || 'This account';

/** The chip colour each role wears on the board. */
function roleKind(role: string): 'soft' | 'success' | 'brand' | 'warn' | 'outline' {
  if (role === 'Doctor') return 'soft';
  if (role === 'Receptionist' || role === 'Nurse') return 'success';
  if (role === 'Billing') return 'brand';
  if (role === 'Clinic Manager' || role === 'Owner') return 'warn';
  return 'outline';
}

export function ManagerRolesPage() {
  const qc = useQueryClient();
  const tenantId = useAuthStore((s) => s.activeTenant?.id ?? null);
  const tenantName = useAuthStore((s) => s.activeTenant?.displayName ?? null);
  const me = useAuthStore((s) => s.user?.id ?? null);
  const myRole = useAuthStore((s) => s.activeTenant?.role ?? null);
  const { members, isLoading } = useMembers();
  const { value: money } = useMoneyPolicy();
  const saveMoney = useSaveMoneyPolicy();

  const [openedId, setOpenedId] = useState<string | null>(null);
  const [limit, setLimit] = useState(5);
  const [query, setQuery] = useState('');
  const [shareFor, setShareFor] = useState<string | null>(null);
  const [inviting, setInviting] = useState(false);
  /* Phone only: a tap on a person opens their account full-screen. */
  const [tapped, setTapped] = useState(false);
  const narrow = useNarrowViewport();
  const [editingMember, setEditingMember] = useState<string | null>(null);
  const [note, setNote] = useState<{ kind: 'ok' | 'bad'; text: string } | null>(null);

  usePageMeta({ title: 'Roles & access · System Settings' });

  /* The board lists people, not memberships: a revoked row is not a person
     with access. They stay countable — "showing 5 of 12" is the live ones. */
  const people = useMemo(
    () => members.filter((m) => m.status !== 'revoked'),
    [members],
  );

  const q = query.trim().toLowerCase();
  const matched = useMemo(
    () => people.filter((m) => !q
      || `${fullName(m)} ${m.roleName ?? ''} ${m.email ?? ''}`.toLowerCase().includes(q)),
    [people, q],
  );
  const shown = matched.slice(0, limit);
  const selected = people.find((m) => m.userId === openedId) ?? shown[0] ?? null;

  const mayAct = (m: MemberRow | null) =>
    !!m && m.userId !== me && !PRIVILEGED.has(m.roleName ?? '');

  /* GRANTING A SWITCH IS THE SAME BAR AS CHANGING THE PRIMARY — the server
     gates both on requireOwner, so the chips are inert for a manager rather
     than clickable and then refused (§4.8). */
  const mayGrant = myRole === 'Owner';

  const [revoking, setRevoking] = useState<MemberRow | null>(null);

  const revoke = useMutation({
    mutationFn: async ({ userId, reason, note }: { userId: string; reason: string; note: string }) => {
      /* DELETE with a body — the verb is still a deletion of the MEMBERSHIP,
         and the reason is metadata about it rather than a resource of its
         own. axios needs `data` for that; fetch would need the same. */
      await apiClient.delete(`/tenants/${tenantId}/members/${userId}`, {
        data: { reason, note: note || undefined },
      });
    },
    onSuccess: () => {
      setNote({ kind: 'ok', text: 'Access closed. Everything they signed, billed or logged stays where it is.' });
      setRevoking(null);
      void qc.invalidateQueries({ queryKey: ['cm-members', tenantId] });
    },
    onError: () => setNote({ kind: 'bad', text: 'That was refused. Nothing has changed.' }),
  });

  const setRoles = useMutation({
    mutationFn: async ({ userId, roleNames }: { userId: string; roleNames: string[] }) => {
      await apiClient.patch(`/tenants/${tenantId}/members/${userId}/roles`, { roleNames });
    },
    onSuccess: () => {
      setNote({ kind: 'ok', text: 'Saved — it applies on their next request, not their next sign-in.' });
      void qc.invalidateQueries({ queryKey: ['cm-members', tenantId] });
    },
    onError: () => setNote({ kind: 'bad', text: 'That was refused. Nothing has changed.' }),
  });

  const setStatus = useMutation({
    mutationFn: async ({ userId, status }: { userId: string; status: 'active' | 'suspended' }) => {
      await apiClient.patch(`/tenants/${tenantId}/members/${userId}/status`, { status });
    },
    onSuccess: (_d, v) => {
      setNote({ kind: 'ok', text: v.status === 'suspended' ? 'Suspended — every open session ended.' : 'Reactivated.' });
      void qc.invalidateQueries({ queryKey: ['cm-members', tenantId] });
    },
    onError: () => setNote({ kind: 'bad', text: 'That was refused. Nothing was changed.' }),
  });

  const resetPassword = useMutation({
    mutationFn: async (userId: string) => {
      await apiClient.post(`/tenants/${tenantId}/members/${userId}/reset-password`, { mode: 'email' });
    },
    onSuccess: () => setNote({ kind: 'ok', text: 'A reset link is on its way to their email.' }),
    onError: () => setNote({ kind: 'bad', text: 'That was refused. No link was sent.' }),
  });

  const invite = useMutation({
    mutationFn: async (body: InviteBody) => {
      const res = await apiClient.post(`/tenants/${tenantId}/invitations`, body);
      return (res.data?.data ?? res.data) as {
        acceptUrl?: string;
        delivered?: boolean;
        deliveryChannel?: string;
      };
    },
    /* THE SLIP IS THE ONLY CHANNEL WHERE THE LINK IS THE PRODUCT. Nothing was
       sent on purpose, so the URL has to arrive somewhere the manager can act
       on it — the clipboard, with the toast saying so — rather than being
       reported as a delivery that silently did not happen. */
    onSuccess: (r) => {
      if (r?.deliveryChannel === 'slip' && r.acceptUrl) {
        void navigator.clipboard?.writeText(r.acceptUrl).catch(() => undefined);
        setNote({ kind: 'ok', text: 'Added. The link is on your clipboard — print it or hand it over.' });
      } else if (r?.delivered === false) {
        setNote({
          kind: 'bad',
          text: 'Added, but the invite did not go out. Open their row to send it again.',
        });
      } else {
        setNote({ kind: 'ok', text: 'Invited — they claim the login themselves.' });
      }
      setInviting(false);
      void qc.invalidateQueries({ queryKey: ['cm-members', tenantId] });
    },
    onError: () => setNote({ kind: 'bad', text: 'That invitation was refused.' }),
  });

  /** "joined 3 Sep · last seen 19 Sep" — the board's second caption line. */
  const accountLine = (m: MemberRow): string => {
    const bits: string[] = [];
    if (m.joinedAt) bits.push(`joined ${stamp(m.joinedAt).split(' ').slice(0, 2).join(' ')}`);
    bits.push(m.lastActiveAt
      ? `last seen ${stamp(m.lastActiveAt).split(' ').slice(0, 2).join(' ')}`
      : 'never signed in');
    if (m.status === 'suspended') bits.push('suspended');
    return bits.join(' · ');
  };

  const shareOf = (userId: string) => money?.payoutShares[userId];

  const pane = selected ? (
    <Pane head={narrow ? undefined : (
      <PaneHead
        overline="THIS ACCOUNT"
        title={fullName(selected)}
        sub={tenantName || 'This clinic'}
      />
    )}>
      <>
        <Card>
          <p className="t-ovl mut mb8">Roles on this one account — additive switches</p>
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
            {ASSIGNABLE.map((r) => {
              const primary = r === selected.roleName;
              const extra = (selected.additionalRoles ?? []).includes(r);
              const on = primary || extra;
              /* THE PRIMARY IS NOT A SWITCH ON THIS CARD. Turning it off would
                 be asking "what is her home screen then?", which is the other
                 verb and the owner's — so it is drawn held, not throwable. */
              return (
                <Chip key={r} kind={on ? 'brand' : 'outline'}
                  className={on ? 'w6' : undefined}
                  onClick={primary || !mayGrant
                    ? undefined
                    : () => setRoles.mutate({
                        userId: selected.userId,
                        roleNames: extra
                          ? (selected.additionalRoles ?? []).filter((x) => x !== r)
                          : [...(selected.additionalRoles ?? []), r],
                      })}>
                  {r}{on ? ' ✓' : ''}{primary ? ' · home' : ''}
                </Chip>
              );
            })}
          </div>
          <p className="t-cap mut mt8">
            A role is a switch, never a second account.{' '}
            {mayGrant
              ? 'The one marked home is where they land; the rest add what they can reach. Granting one is the owner’s act, like changing the primary.'
              : 'Adding or removing one is the tenant owner’s act — the same decision as changing which role they land on.'}
          </p>
        </Card>

        <MemberProfileCards member={selected} tenantId={tenantId} onNote={setNote}
          doctors={people.filter((m) => m.roleName === 'Doctor')} />

        <Card>
          <p className="t-ovl mut mb8">Account</p>
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
            <Chip kind={selected.status === 'active' ? 'success' : 'warn'} small>
              {selected.status === 'active' ? 'Active' : 'Suspended'}
            </Chip>
            <DChip
              disabled={!mayAct(selected) || resetPassword.isPending}
              onClick={() => resetPassword.mutate(selected.userId)}>
              <Icon name="mail" size="sm" /> Reset password
            </DChip>
          </div>
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 8 }}>
            <DChip kind="warn" disabled={!mayAct(selected) || setStatus.isPending}
              onClick={() => setStatus.mutate({
                userId: selected.userId,
                status: selected.status === 'active' ? 'suspended' : 'active',
              })}>
              <Icon name="ban" size="sm" /> {selected.status === 'active' ? 'Suspend' : 'Reactivate'}
            </DChip>
            {/* Not disabled — a chip that cannot be pressed cannot say why.
                It is pressable and it answers, which is the honest shape of a
                refusal this screen knows about in advance (§4.8). */}
            <DChip kind="danger"
              onClick={() => (mayGrant || mayAct(selected)
                ? setRevoking(selected)
                : setNote({
                    kind: 'bad',
                    text: 'Removing a membership is the tenant owner’s act. Suspend ends their access today; only the owner can take the seat back.',
                  }))}>
              <Icon name="user-off" size="sm" /> Revoke
            </DChip>
          </div>
          <p className="t-cap mut mt8">
            Suspend and Revoke end every open session immediately — that is the difference between
            this and simply turning the roles off.
          </p>
          {/* The board draws three more chips here — Devices · 2,
              Notifications and Reset signing PIN. None of the three exists
              for somebody ELSE's account: no device register is kept, a
              person's notification preferences are their own, and the signing
              PIN is self-service only (`/users/me/signing-pin`). Naming them
              is more use than three chips that answer nothing. */}
          <p className="t-cap mut mt4">
            Devices, notification preferences and the signing PIN are the person’s own — no screen
            here reaches them, and the PIN can only ever be reset by its holder.
          </p>
        </Card>
      </>
    </Pane>
  ) : undefined;

  const crumb = usePhoneCrumb('Settings', 'Roles & access');
  useShellChrome(
    {
      raw: true,
      ...crumb,
      phoneMenu: (close) => <ManagerMenu door="settings" onClose={close} />,
      paneOpen: tapped && !narrow,
      onPaneClose: () => setTapped(false),
      paneTitle: selected ? fullName(selected) : 'This account',
      title: 'Roles & access',
      sub: 'one human, one login — roles are switches',
      search: 'Search a person, a role, a branch…',
      searchValue: query,
      onSearchChange: setQuery,
      subrail: <SettingsRail active="roles" />,
      primary: { label: '+ Add person', onClick: () => setInviting(true) },
      pane: narrow ? undefined : pane,
    },
    [
      narrow, query, tapped, openedId ?? '', selected?.userId ?? '', selected?.status ?? '',
      selected?.roleName ?? '', people.length, limit,
      setStatus.isPending, resetPassword.isPending,
    ],
  );

  return (
    <div className="maie desk">
      <ManagerSurface />
      <div className="dcontent">
        {isLoading && !people.length ? (
          <p className="t-sec mut" style={{ marginTop: 18 }}>Reading who works here…</p>
        ) : null}

        {people.length ? (
          <>
            {narrow ? (
              <div className="cm-pcards">
                {shown.map((m) => {
                  const open = tapped && m.userId === selected?.userId;
                  const share = shareOf(m.userId);
                  return (
                    <div key={m.userId}
                      className={`cm-pcard${open ? ' sel' : ''}`}
                      onClick={() => { setOpenedId(m.userId); setTapped(true); }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8 }}>
                        <div style={{ minWidth: 0 }}>
                          <p className="t-strong">{fullName(m)}</p>
                          <p className="t-cap mut" style={{ marginTop: 2 }}>{tenantName || 'This clinic'}</p>
                        </div>
                        {/* The card opens on tap; this button acts on the person without opening it. */}
                        <span onClick={(e) => e.stopPropagation()}>
                          <DChip onClick={() => (m.roleName === 'Doctor'
                            ? setShareFor(m.userId)
                            : setEditingMember(m.userId))}>
                            {m.roleName === 'Doctor'
                              ? `Payout ${share === undefined ? '' : `${share}% `}✎`
                              : 'Edit'}
                          </DChip>
                        </span>
                      </div>
                      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 8 }}>
                        <Chip kind={roleKind(m.roleName ?? '')} small>{m.roleName ?? 'No role'}</Chip>
                        {(m.additionalRoles ?? []).map((r) => (
                          <Chip key={r} kind="outline" small>{r}</Chip>
                        ))}
                      </div>
                      <p className="t-cap mut" style={{ marginTop: 8 }}>{accountLine(m)}</p>
                      {open && pane ? (
                        <div className="cm-pcard-body" onClick={(e) => e.stopPropagation()}>{pane}</div>
                      ) : null}
                    </div>
                  );
                })}
              </div>
            ) : (
            <div className="cm-tscroll">
              <table className="dtable cm-fixed-cols">
                <thead>
                  <tr>
                    <th style={{ width: '28%' }}>Person</th>
                    <th style={{ width: '48%' }}>Roles</th>
                    <th style={{ width: '24%' }} />
                  </tr>
                </thead>
                <tbody>
                  {shown.map((m) => {
                    const isDoctor = m.roleName === 'Doctor';
                    const share = shareOf(m.userId);
                    return (
                      <tr key={m.userId}
                        className={`rowc${m.userId === selected?.userId ? ' sel' : ''}`}
                        onClick={() => { setOpenedId(m.userId); setTapped(true); }}>
                        <td>
                          <p className="t-strong cm-one-line">{fullName(m)}</p>
                          <p className="t-cap mut" style={{ marginTop: 4 }}>
                            {tenantName || 'This clinic'}
                          </p>
                        </td>
                        <td>
                          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', rowGap: 6 }}>
                            <Chip kind={roleKind(m.roleName ?? '')} small>
                              {m.roleName ?? 'No role'}
                            </Chip>
                            {/* THE EXTRA SWITCHES BELONG ON THE LIST, not only in
                                the pane: a manager cannot tell a receptionist who
                                also does the billing from one who does not
                                without opening every row. Outlined, so the one
                                they LAND on still reads as the primary. */}
                            {(m.additionalRoles ?? []).map((r) => (
                              <Chip key={r} kind="outline" small>{r}</Chip>
                            ))}
                          </div>
                          <p className="t-cap mut" style={{ marginTop: 10 }}>{accountLine(m)}</p>
                        </td>
                        <td>
                          <DChip onClick={() => (isDoctor
                            ? setShareFor(m.userId)
                            : setEditingMember(m.userId))}>
                            {isDoctor
                              ? `Payout ${share === undefined ? '' : `${share}% `}✎`
                              : 'Edit'}
                          </DChip>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            )}

            <div className="dt-more">
              showing {shown.length} of {matched.length}
              {matched.length > shown.length ? (
                <>
                  {' · '}
                  <button type="button" className="lnk"
                    style={{ cursor: 'pointer', background: 'none', border: 0, padding: 0, fontFamily: 'inherit', fontSize: 'inherit', lineHeight: 'inherit' }}
                    onClick={() => setLimit((n) => n + 5)}>
                    Load more
                  </button>
                </>
              ) : null}
            </div>

            <p className="t-sec mut" style={{ marginTop: 12 }}>
              Roles are switches on one login. The payout share sits on the doctor’s row because it
              is a property of the person, and it is edited in Money.
            </p>
          </>
        ) : null}

        {!isLoading && !people.length ? (
          <p className="t-sec mut" style={{ marginTop: 18 }}>
            Nobody holds a login in this clinic yet. “+ Add person” sends the first invitation —
            the invite is the first step, and the person sets their own password.
          </p>
        ) : null}

        {note ? (
          <p className={note.kind === 'ok' ? 't-sec grn' : 't-sec red'} style={{ marginTop: 10 }}>
            {note.text}
          </p>
        ) : null}
      </div>

      <TextEditor
        open={shareFor !== null}
        title="Payout share"
        sub="the percentage of collected money this doctor is paid — the same figure Money edits"
        value={shareFor && money?.payoutShares[shareFor] !== undefined
          ? String(money.payoutShares[shareFor]) : ''}
        onClose={() => setShareFor(null)}
        onDone={(next) => {
          const id = shareFor;
          if (!id || !money) return;
          const n = Number(next);
          if (!next || !Number.isFinite(n) || n < 0 || n > 100) {
            setNote({ kind: 'bad', text: 'A share is a percentage between 0 and 100.' });
            return;
          }
          saveMoney.mutate(
            { payoutShares: { ...money.payoutShares, [id]: n } },
            {
              onSuccess: () => setNote({ kind: 'ok', text: 'Share saved — it applies from the next run onward.' }),
              onError: () => setNote({ kind: 'bad', text: 'That did not save.' }),
            },
          );
        }}
      />

      {/* `g_m_revoke` (23c) — the reason list, ending in Other. The consequence
          column is not decoration: "left the clinic" and "password
          compromised" are the same status and entirely different events, and
          the row the manager picks is what answers the question six months
          later. */}
      <ReasonTable
        open={!!revoking}
        onClose={() => setRevoking(null)}
        title={`Revoke access — ${revoking ? fullName(revoking) : ''}`}
        sub="Everything they signed, billed or logged stays exactly where it is. Revoking closes the login, it never edits the record."
        reasons={REVOKE_REASONS}
        notePlaceholder="they keep their ledger history — revoking never removes what a person did"
        confirmLabel="Revoke access"
        busy={revoke.isPending}
        onConfirm={(reason, note) =>
          revoking && revoke.mutate({ userId: revoking.userId, reason: reason.key, note })
        }
      />

      <InvitePerson
        open={inviting}
        onClose={() => setInviting(false)}
        pending={invite.isPending}
        onInvite={(body) => invite.mutate(body)}
      />

      <Modal
        open={editingMember !== null}
        onClose={() => setEditingMember(null)}
        title="Edit this person"
        sub={editingMember ? fullName(people.find((m) => m.userId === editingMember)!) : ''}
        width={460}
        foot={<button type="button" className="btn btn-outline" onClick={() => setEditingMember(null)}>Close</button>}
      >
        <div style={{ marginTop: 12 }}>
          <p className="t-sec sec">
            What a manager may change about somebody else is their access, not their identity:
            suspend or reactivate them, and send them a password reset. Both are in the pane beside
            this list, on the row you selected.
          </p>
          <p className="t-cap mut mt8">
            Moving a person from one role to another, and removing them altogether, are the tenant
            owner’s — a manager may not mint peers.
          </p>
        </div>
      </Modal>
    </div>
  );
}

/**
 * ADD PERSON — `g_m_addp` (23b). "A name, a number and at least one role —
 * everything else can wait until they sign in."
 *
 * Its own component with its own state: a form whose state lived in the page
 * would re-render the page, and the pane, on every keystroke (BRIEF §4.3).
 *
 * THE SWITCHES ARE ADDITIVE AND THE FIRST ONE IS THE PRIMARY. That is not a
 * UI convention, it is the model: the primary role is the person's home screen
 * and sidebar, the extras add their permissions to the same login. So the chip
 * that was turned on first wears "home", and turning it off promotes whichever
 * is still on — a person cannot end up with permissions and nowhere to land.
 *
 * BRANCHES are chips under the mobile: none chosen means every branch, and the
 * choice is written to `staff_branches` when the invitation is accepted.
 */
function InvitePerson({
  open, onClose, pending, onInvite,
}: {
  open: boolean;
  onClose: () => void;
  pending: boolean;
  onInvite: (body: InviteBody) => void;
}) {
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');
  const [channel, setChannel] = useState<InviteChannel>('whatsapp');
  /* Ordered, not a Set: the first switch on is the primary, and that has to
     survive turning a later one off. */
  const [roles, setRoles] = useState<string[]>(['Receptionist']);
  const [branchIds, setBranchIds] = useState<string[]>([]);
  const branches = useBranches().filter((b): b is typeof b & { id: string } => !!b.id);
  if (!open) return null;

  const toggle = (r: string) =>
    setRoles((cur) => (cur.includes(r) ? cur.filter((x) => x !== r) : [...cur, r]));

  const [first, ...rest] = name.trim().split(/\s+/).filter(Boolean);
  const phoneOk = phone.replace(/[^\d+]/g, '').length >= 8;
  const emailOk = /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email.trim());
  /* What each channel actually needs to reach them. A slip needs no address at
     all beyond one the account can be found by later, so it asks for whichever
     the manager has. */
  const addressed = channel === 'email' ? emailOk : channel === 'whatsapp' ? phoneOk : phoneOk || emailOk;
  const ok = !!first && roles.length > 0 && addressed;

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Add person"
      sub="a name, a number and at least one role — everything else can wait until they sign in"
      width={760}
      foot={(
        <>
          <button type="button" className="btn btn-outline" onClick={onClose}>Cancel</button>
          <button type="button" className="btn btn-primary" disabled={!ok || pending}
            onClick={() => onInvite({
              firstName: first,
              lastName: rest.join(' ') || undefined,
              phone: phoneOk ? phone.trim() : undefined,
              email: emailOk ? email.trim() : undefined,
              deliveryChannel: channel,
              roleName: roles[0],
              additionalRoleNames: roles.slice(1),
              branchIds: branchIds.length ? branchIds : undefined,
            })}>
            {pending ? 'Sending…' : channel === 'slip' ? 'Add & make a slip' : 'Add & send invite'}
          </button>
        </>
      )}
    >
      <div className="cm-addp">
        <div className="cm-addp-col">
          <label className="t-ovl mut" htmlFor="cm-invite-name">Full name</label>
          <input id="cm-invite-name" className="cm-inp" value={name} autoFocus
            placeholder="Nadia Hassan" onChange={(e) => setName(e.target.value)} />

          <label className="t-ovl mut mt12" htmlFor="cm-invite-phone">
            Mobile — the invite goes here
          </label>
          <input id="cm-invite-phone" className="cm-inp" type="tel" value={phone}
            placeholder="0100 777 3311" onChange={(e) => setPhone(e.target.value)} />

          {branches.length > 1 ? (
            <>
              <p className="t-ovl mut mt12">Branches</p>
              <div style={{ display: 'flex', gap: 6, marginTop: 8, flexWrap: 'wrap' }}>
                <Chip kind={branchIds.length === 0 ? 'brand' : 'outline'} small
                  className={branchIds.length === 0 ? 'w6' : undefined}
                  onClick={() => setBranchIds([])}>
                  All branches
                </Chip>
                {branches.map((b) => {
                  const on = branchIds.includes(b.id);
                  return (
                    <Chip key={b.id} kind={on ? 'brand' : 'outline'} small
                      className={on ? 'w6' : undefined}
                      onClick={() => setBranchIds((cur) => (on ? cur.filter((x) => x !== b.id) : [...cur, b.id]))}>
                      {b.name}{on ? ' ✓' : ''}
                    </Chip>
                  );
                })}
              </div>
            </>
          ) : null}

          <label className="t-ovl mut mt12" htmlFor="cm-invite-email">
            Email {channel === 'email' ? '' : <span className="mut">— optional</span>}
          </label>
          <input id="cm-invite-email" className="cm-inp" type="email" value={email}
            placeholder="nadia@clinic.example" onChange={(e) => setEmail(e.target.value)} />
          {/* THE ONE THING THE BOARD LEAVES UNSAID and the person will ask:
              where does the password come from. It comes from them. */}
          <p className="t-cap mut mt4">
            They choose the address they sign in with when they accept. Give one here only if you
            already know it.
          </p>

          <p className="t-ovl mut mt12">Send the invite over</p>
          <div style={{ display: 'flex', gap: 6, marginTop: 8, flexWrap: 'wrap' }}>
            {CHANNELS.map((c) => (
              <Chip key={c.key} kind={channel === c.key ? 'brand' : 'outline'} small
                className={channel === c.key ? 'w6' : undefined}
                onClick={() => setChannel(c.key)}>
                {c.label}
              </Chip>
            ))}
          </div>
          <p className="t-cap mut mt4">
            {channel === 'whatsapp'
              ? 'a WhatsApp invite spends one message from the wallet'
              : channel === 'email'
                ? 'an email costs nothing and waits in their inbox'
                : 'nothing is sent — you get the link to print and hand over'}
          </p>
        </div>

        <div className="cm-addp-col">
          <div className="card" style={{ margin: 0 }}>
            <p className="t-ovl mut mb8">Roles on this one account — additive switches</p>
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
              {/* Owner and Clinic Manager are absent on purpose: a manager may
                  not create a peer, and the server refuses it — offering the
                  choice and then being refused is the failure §4.8 names. */}
              {INVITABLE.map((r) => {
                const on = roles.includes(r);
                return (
                  <Chip key={r} kind={on ? 'brand' : 'outline'} small={false}
                    className={on ? 'w6' : undefined} onClick={() => toggle(r)}>
                    {r}{on ? ' ✓' : ''}{roles[0] === r ? ' · home' : ''}
                  </Chip>
                );
              })}
            </div>
            <p className="t-cap mut mt8">
              A role is a switch, never a second account.{' '}
              {roles.length > 1
                ? `${roles[0]} is their home screen; the rest add what they can reach.`
                : 'The first one on is their home screen.'}
            </p>
          </div>

          <Card kind="accent" style={{ marginTop: 12 }}>
            <p className="t-sec brand">
              The invite is the first step, not an afterthought: they confirm the number, set a
              password, set a signing PIN if they are a doctor, and land on their role&rsquo;s home.
            </p>
          </Card>
        </div>
      </div>
    </Modal>
  );
}
